import { HolderOutlined } from "@ant-design/icons";
import {
	Announcements,
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	Modifiers,
	PointerSensor,
	UniqueIdentifier,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	SortingStrategy,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CSSProperties, ReactNode, useMemo, useSyncExternalStore } from "react";

/*
 * Whether an item is being dragged, for the editor's modals: Ant's dialog
 * stops Escape from going further, so while a drag is on they turn
 * Escape-to-close off, and Escape cancels the drag instead.
 */
let dragsInProgress = 0;
const dragListeners = new Set<() => void>();

const countDrag = (change: 1 | -1) => {
	dragsInProgress = Math.max(0, dragsInProgress + change);
	dragListeners.forEach((listener) => listener());
};

const subscribeToDrags = (listener: () => void) => {
	dragListeners.add(listener);
	return () => {
		dragListeners.delete(listener);
	};
};

export const isDragInProgress = () => dragsInProgress > 0;

/** For a drag-and-drop of its own (the markdown tab bar): a drag started or ended. */
export const noteDragStart = () => countDrag(1);
export const noteDragEnd = () => countDrag(-1);

export const useDragInProgress = () =>
	useSyncExternalStore(subscribeToDrags, isDragInProgress);

interface SortableListProps {
	ids: string[];
	/** The moved item's id, and the id of the item it was dropped on. */
	onMove: (activeId: string, overId: string) => void;
	/** What a screen reader calls an item, such as its title. */
	nameOf: (id: string) => string;
	strategy?: SortingStrategy;
	/** Constraints on the drag, such as moving up and down only. */
	modifiers?: Modifiers;
	/** Reordering is off, as while the list is filtered. */
	disabled?: boolean;
	children: ReactNode;
}

/**
 * Items that reorder by their drag handles, with the pointer (after an 8 px
 * move, so a click is still a click) or the keyboard: Space to lift, the
 * arrow keys to move, Space to drop, Escape to cancel.
 */
export function SortableList({
	ids,
	onMove,
	nameOf,
	strategy = verticalListSortingStrategy,
	modifiers,
	disabled = false,
	children,
}: Readonly<SortableListProps>) {
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);
	// Spoken in the item's name, not its id.
	const announcements = useMemo<Announcements>(() => {
		const name = (id: UniqueIdentifier) => nameOf(String(id));
		return {
			onDragStart: ({ active }) => `Picked up ${name(active.id)}.`,
			onDragOver: ({ active, over }) =>
				over
					? `${name(active.id)} is over ${name(over.id)}.`
					: `${name(active.id)} is no longer over another item.`,
			onDragEnd: ({ active, over }) =>
				over
					? `${name(active.id)} was dropped on ${name(over.id)}.`
					: `${name(active.id)} was dropped.`,
			onDragCancel: ({ active }) => `Moving ${name(active.id)} was cancelled.`,
		};
	}, [nameOf]);

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		noteDragEnd();
		if (over && active.id !== over.id) {
			onMove(String(active.id), String(over.id));
		}
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			modifiers={modifiers}
			accessibility={{ announcements }}
			onDragStart={noteDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={noteDragEnd}
		>
			<SortableContext items={ids} strategy={strategy} disabled={disabled}>
				{children}
			</SortableContext>
		</DndContext>
	);
}

/** A sortable item: its ref and style, and the props for its drag handle. */
export const useSortableRow = (id: string) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });
	const style: CSSProperties = {
		transform: CSS.Translate.toString(transform),
		transition,
		...(isDragging ? { position: "relative", zIndex: 2 } : {}),
	};
	return {
		setNodeRef,
		style,
		isDragging,
		handleProps: { ref: setActivatorNodeRef, ...attributes, ...listeners },
	};
};

interface DragHandleProps {
	/** Such as "Reorder Getting started". */
	label: string;
	handleProps: ReturnType<typeof useSortableRow>["handleProps"];
	disabled?: boolean;
}

/** The button that drags an item: with the pointer, or Space and the arrow keys. */
export function DragHandle({
	label,
	handleProps,
	disabled = false,
}: Readonly<DragHandleProps>) {
	const { ref, ...handle } = handleProps;
	return (
		<button
			type="button"
			ref={ref}
			className="help-handle"
			aria-label={label}
			disabled={disabled}
			{...handle}
		>
			<HolderOutlined aria-hidden="true" />
		</button>
	);
}
